import { useFormContext } from "react-hook-form";
import { useState, useMemo, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Consultation } from "@/types/consultation";
import Model, { IMuscleStats, IExerciseData, Muscle } from "react-body-highlighter";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

// Body parts mapping
const BODY_PARTS = {
  // Back
  trapezius: "Trapezius",
  "upper-back": "Upper Back",
  "lower-back": "Lower Back",
  // Chest
  chest: "Chest",
  // Arms
  biceps: "Biceps",
  triceps: "Triceps",
  forearm: "Forearm",
  "back-deltoids": "Back Deltoids",
  "front-deltoids": "Front Deltoids",
  // Abs
  abs: "Abs",
  obliques: "Obliques",
  // Legs
  adductor: "Adductor",
  hamstring: "Hamstring",
  quadriceps: "Quadriceps",
  abductors: "Abductors",
  calves: "Calves",
  gluteal: "Gluteal",
  // Head
  head: "Head",
  neck: "Neck",
} as const;

const BodyMeasurements = ({ action, open }: FormStepProps) => {
  const { watch, setValue } = useFormContext<Consultation>();
  const [selectedBodyPart, setSelectedBodyPart] = useState<string | null>(null);
  const [measurementValue, setMeasurementValue] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const measurementFormRef = useRef<HTMLFormElement>(null);

  const bodyMeasurements = watch("body_measurements") || {};

  // Convert body measurements to IExerciseData[] format for highlighting
  const exerciseData = useMemo<IExerciseData[]>(() => {
    return Object.keys(bodyMeasurements).map((muscle) => ({
      name: `${BODY_PARTS[muscle as keyof typeof BODY_PARTS] || muscle}: ${bodyMeasurements[muscle]} cm`,
      muscles: [muscle as Muscle],
      frequency: 1,
    }));
  }, [bodyMeasurements]);

  // Handle body part click
  const handleMuscleClick = ({ muscle }: IMuscleStats) => {
    const bodyPartKey = muscle as keyof typeof BODY_PARTS;
    if (BODY_PARTS[bodyPartKey]) {
      setSelectedBodyPart(bodyPartKey);
      setMeasurementValue(
        bodyMeasurements[bodyPartKey]?.toString() || ""
      );
      setIsDialogOpen(true);
    }
  };

  // Handle save measurement
  const handleSaveMeasurement = () => {
    if (selectedBodyPart && measurementValue) {
      const value = parseFloat(measurementValue);
      if (!isNaN(value) && value > 0) {
        setValue("body_measurements", {
          ...bodyMeasurements,
          [selectedBodyPart]: value,
        });
        setIsDialogOpen(false);
        setSelectedBodyPart(null);
        setMeasurementValue("");
      }
    }
  };

  // Handle form submission (Enter key)
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleSaveMeasurement();
  };

  // Handle remove measurement
  const handleRemoveMeasurement = () => {
    if (selectedBodyPart) {
      const updated = { ...bodyMeasurements };
      delete updated[selectedBodyPart];
      setValue("body_measurements", updated);
      setIsDialogOpen(false);
      setSelectedBodyPart(null);
      setMeasurementValue("");
    }
  };

  // Handle edit measurement from saved list
  const handleEditMeasurement = (bodyPartKey: string) => {
    setSelectedBodyPart(bodyPartKey);
    setMeasurementValue(bodyMeasurements[bodyPartKey]?.toString() || "");
    setIsDialogOpen(true);
  };

  // Handle remove measurement from saved list
  const handleRemoveFromList = (bodyPartKey: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering edit when clicking remove
    const updated = { ...bodyMeasurements };
    delete updated[bodyPartKey];
    setValue("body_measurements", updated);
  };

  // Get selected body part display name
  const selectedBodyPartName = selectedBodyPart
    ? BODY_PARTS[selectedBodyPart as keyof typeof BODY_PARTS]
    : "";

  return (
    <div className="space-y-4">
      {/* Add CSS for hover effects on body parts */}
      <style>{`
        .rbh-wrapper svg polygon {
          transition: fill 0.2s ease, opacity 0.2s ease, filter 0.2s ease;
        }
        .rbh-wrapper svg polygon:hover {
          filter: brightness(0.7);
          opacity: 0.9;
        }
      `}</style>

      <Card className="p-4">
        <p className="font-medium text-sm">Body Measurements</p>
        <p className="text-xs text-gray-500 mt-1">
          Click on any body part to add measurements (in centimeters).
        </p>
        <Separator className="h-[1px] mt-2" />

        {/* Side by side front and back views */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {/* Front View */}
          <div className="flex flex-col items-center">
            <p className="text-sm font-medium mb-2">Front View</p>
            <div className="flex justify-center items-center w-full">
              <Model
                data={exerciseData}
                onClick={handleMuscleClick}
                highlightedColors={["#77dd77", "#60a5fa"]} // Green/Blue colors for selected parts
                style={{ width: "100%", maxWidth: "300px", height: "450px" }}
                svgStyle={{ cursor: "pointer" }}
              />
            </div>
          </div>

          {/* Back View */}
          <div className="flex flex-col items-center">
            <p className="text-sm font-medium mb-2">Back View</p>
            <div className="flex justify-center items-center w-full">
              <Model
                type="posterior"
                data={exerciseData}
                onClick={handleMuscleClick}
                highlightedColors={["#77dd77", "#60a5fa"]} // Green/Blue colors for selected parts
                style={{ width: "100%", maxWidth: "300px", height: "450px" }}
                svgStyle={{ cursor: "pointer" }}
              />
            </div>
          </div>
        </div>

        {/* Info about views */}
        <div className="mt-4 p-3 bg-blue-50 rounded text-xs text-gray-600">
          <p className="font-medium mb-1">Note:</p>
          <p>
            Front and back views show different body parts. Measurements are shared between views - 
            if you measure a part on the front view, it will also appear highlighted on the back view if that part is visible there.
            Some parts like head, neck, and some leg muscles appear on both views.
          </p>
        </div>

        {/* Display saved measurements */}
        {Object.keys(bodyMeasurements).length > 0 && (
          <div className="mt-6">
            <p className="text-sm font-medium mb-2">Saved Measurements:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(bodyMeasurements).map(([key, value]) => (
                <div
                  key={key}
                  onClick={() => handleEditMeasurement(key)}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm cursor-pointer hover:bg-gray-100 transition-colors group relative"
                >
                  <div className="flex gap-1 min-w-0">
                    <span className="capitalize block truncate">
                      {BODY_PARTS[key as keyof typeof BODY_PARTS] || key}:
                    </span>
                    <span className="font-medium ">{value} cm</span>
                  </div>
                  <button
                    onClick={(e) => handleRemoveFromList(key, e)}
                    className="ml-2 p-1 rounded hover:bg-red-100 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    type="button"
                    title="Remove measurement"
                  >
                    <i className="fa fa-trash text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Measurement Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {selectedBodyPartName
                ? `Measure ${selectedBodyPartName}`
                : "Add Measurement"}
            </DialogTitle>
          </DialogHeader>
          <form
            ref={measurementFormRef}
            onSubmit={handleFormSubmit}
            onKeyDown={(e) => {
              // Prevent form submission from bubbling to parent form
              if (e.key === "Enter" && e.target === e.currentTarget) {
                e.stopPropagation();
              }
            }}
          >
            <div className="space-y-4 py-4">
              <FloatingLabelInput
                id="measurement"
                type="number"
                label="Measurement (cm)"
                value={measurementValue}
                onChange={(e) => setMeasurementValue(e.target.value)}
                min="0"
                step="0.1"
                placeholder="Enter measurement in centimeters"
                onKeyDown={(e) => {
                  // Prevent Enter key from triggering parent form
                  if (e.key === "Enter") {
                    e.stopPropagation();
                  }
                }}
              />
            </div>
            <DialogFooter>
              {bodyMeasurements[selectedBodyPart || ""] && (
                <Button
                  variant="destructive"
                  onClick={handleRemoveMeasurement}
                  type="button"
                >
                  Remove
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  setSelectedBodyPart(null);
                  setMeasurementValue("");
                }}
                type="button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!measurementValue || parseFloat(measurementValue) <= 0}
              >
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BodyMeasurements;

